import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRecommendationsFeed } from "../../api/recommendationService";
import ProductCard from "./ProductCard";
import Skeleton from "./Skeleton";
import "./clientTheme.css";
import "./HomeFeedGrid.css";

const PAGE_SIZE = 24;

// Endless, paginated grid shown at the bottom of Home: products personalized from
// the user's recent search/view/purchase history, or a shuffled cross-category mix
// for a brand-new visitor with no history yet. Loads more as the user scrolls near
// the bottom instead of fetching the whole catalog up front.
export default function HomeFeedGrid({ title }) {
  const { t } = useTranslation("common");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const skipRef = useRef(0);
  const seedRef = useRef(Math.floor(Math.random() * 99999) + 1);
  const sentinelRef = useRef(null);

  const loadFeed = useCallback((reset) => {
    if (reset) {
      skipRef.current = 0;
      setHasMore(true);
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    return getRecommendationsFeed({ skip: reset ? 0 : skipRef.current, take: PAGE_SIZE, seed: seedRef.current })
      .then((items) => {
        skipRef.current = (reset ? 0 : skipRef.current) + items.length;
        setHasMore(items.length === PAGE_SIZE);
        setProducts((prev) => (reset ? items : [...prev, ...items]));
      })
      .catch(() => {
        if (reset) setProducts([]);
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, []);

  useEffect(() => {
    loadFeed(true);
  }, [loadFeed]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore && hasMore) {
          loadFeed(false);
        }
      },
      { rootMargin: "600px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, loadFeed]);

  if (!loading && products.length === 0) return null;

  return (
    <section className="product-shelf home-feed-grid-section">
      <div className="product-shelf-header">
        <h2 className="product-shelf-title">{title}</h2>
      </div>

      {loading ? (
        <div className="home-feed-grid">
          {Array.from({ length: 12 }).map((_, index) => (
            <div className="home-feed-grid-skeleton-card" key={index}>
              <Skeleton className="product-shelf-skeleton-image" />
              <Skeleton className="product-shelf-skeleton-line" />
              <Skeleton className="product-shelf-skeleton-line product-shelf-skeleton-line-short" />
            </div>
          ))}
        </div>
      ) : (
        <div className="home-feed-grid">
          {products.map((product) => (
            <ProductCard product={product} key={product.productId} />
          ))}
        </div>
      )}

      {loadingMore && (
        <div className="home-feed-grid-spinner" role="status" aria-label={t("loadingMoreProducts")} />
      )}

      <div ref={sentinelRef} className="home-feed-grid-sentinel" aria-hidden="true" />
    </section>
  );
}
